import {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {getMemberInfo, changePhone} from '../../../api/memberApi';
import {alertAfterFunction, defaultAlert} from "../../../utils/alert";
import {getPassAuthUrl, generateRequestNo} from '../../../api/passApi';

const PhoneChangePage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const passVerifyData = location.state?.passVerifyData;

    const [memberName, setMemberName] = useState<string>('');
    const [currentPhone, setCurrentPhone] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);

    // 회원 정보 조회
    useEffect(() => {
        fetchMemberInfo();
    }, []);

    // PASS 인증 결과가 있으면 자동으로 처리
    useEffect(() => {
        if (passVerifyData && memberName) {
            handlePassResult();
        }
    }, [passVerifyData, memberName]);

    const fetchMemberInfo = async () => {
        try {
            const response = await getMemberInfo();
            const data = response.data.data;
            setMemberName(data.memberName);
            setCurrentPhone(data.phone);
        } catch (err) {
            console.error('회원 정보 조회 실패:', err);
            defaultAlert('회원 정보를 불러올 수 없습니다.');
        } finally {
            setLoading(false);
        }
    };

    // PASS 인증 결과 처리
    const handlePassResult = async () => {
        if (processing) return;
        setProcessing(true);

        try {
            const niceData = passVerifyData?.data || {};
            const verifiedName = niceData.name || '';
            const newPhone = niceData.mobile_no || '';

            if (!verifiedName || !newPhone) {
                defaultAlert('본인인증 정보가 올바르지 않습니다.');
                setProcessing(false);
                return;
            }

            // 이름 비교
            if (verifiedName !== memberName) {
                defaultAlert('본인인증 정보가 회원 정보와 일치하지 않습니다.\n가입 시 등록한 이름으로 인증해 주세요.');
                setProcessing(false);
                return;
            }

            // 현재 번호와 동일한지 확인
            if (newPhone === currentPhone) {
                defaultAlert('현재 등록된 휴대폰번호와 동일합니다.');
                setProcessing(false);
                return;
            }

            // 백엔드에 번호 변경 요청
            const response = await changePhone(verifiedName, newPhone);
            const result = response.data;

            if (result.code === '00') {
                alertAfterFunction(
                    '휴대폰번호가 성공적으로 변경되었습니다.',
                    () => navigate('/menu/myInfo', {replace: true})
                );
            } else {
                defaultAlert(result.message || '휴대폰번호 변경에 실패했습니다.');
                setProcessing(false);
            }
        } catch (error: any) {
            console.error('휴대폰번호 변경 실패:', error);
            defaultAlert('휴대폰번호 변경 중 오류가 발생했습니다.');
            setProcessing(false);
        }
    };

    // PASS 본인인증 시작
    const handleStartVerify = async () => {
        sessionStorage.setItem('niceReturnPath', '/mypage/phone-change');
        try {
            const requestNo = generateRequestNo();
            sessionStorage.setItem('passRequestNo', requestNo);
            const closeUrl = `${window.location.origin}/signup/terms`;
            const response = await getPassAuthUrl(requestNo, closeUrl);
            window.location.href = response.authUrl;
        } catch (err) {
            console.error('[NICE] 인증 URL 발급 실패:', err);
            defaultAlert('본인인증 URL 발급에 실패했습니다. 다시 시도해 주세요.');
        }
    };

    // 휴대폰번호 마스킹 (010-1234-5678 → 010-****-5678)
    const maskPhone = (phone: string) => {
        if (!phone) return '-';
        const cleaned = phone.replace(/-/g, '');
        if (cleaned.length === 11) {
            return `${cleaned.substring(0, 3)}-****-${cleaned.substring(7)}`;
        }
        return phone;
    };

    if (loading) {
        return (
            <main className="container">
                <div className="inner-box">
                    <p>로딩 중...</p>
                </div>
            </main>
        );
    }

    return (
        <>
            <div className="bottom-btn">
                <button
                    type="button"
                    className="btn confirm"
                    onClick={handleStartVerify}
                    disabled={processing}
                >
                    {processing ? '처리중...' : '본인인증'}
                </button>
            </div>

            <main className="container">
                <section className="tit-box-wrap">
                    <h2 className="headline">휴대폰번호 변경</h2>
                    <p className="subtit">
                        본인인증을 통해 새로운 휴대폰번호로<br/>
                        변경할 수 있습니다.
                    </p>
                </section>

                <form className="form-box">
                    <section className="bg-white">
                        <fieldset>
                            <legend>휴대폰번호 변경 폼</legend>
                            <div className="inner-box">
                                <div className="form-item">
                                    <label className="input-tit">이름</label>
                                    <div className="input-box">
                                        <input type="text" value={memberName} disabled/>
                                    </div>
                                </div>
                                <div className="form-item">
                                    <label className="input-tit">현재 휴대폰번호</label>
                                    <div className="input-box">
                                        <input type="text" value={maskPhone(currentPhone)} disabled/>
                                    </div>
                                </div>

                                <div className="form-item">
                                    <ul className="guidF-list">
                                        <li>본인인증 완료 후 인증된 휴대폰번호로 자동 변경됩니다.</li>
                                        <li>반드시 회원가입 시 등록한 이름과 동일한 명의의 휴대폰으로 인증해 주세요.</li>
                                    </ul>
                                </div>
                            </div>
                        </fieldset>
                    </section>
                </form>
            </main>
        </>
    );
};

export default PhoneChangePage;
